import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL: string =
  process.env.REACT_APP_API_URL || 'http://0.0.0.0:8000/api/';

const API: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Access token cache — primarily in-memory (XSS-safe), with a sessionStorage
// backup so page reloads within the same tab don't have to refresh the JWT.
// Refreshing on every page reload was racing JWT rotation: rapid patient
// navigation triggers full page reloads (CustomViewer), and the resulting
// chain of refreshes occasionally raced and 401'd the session.
// sessionStorage is cleared on tab close, so the exposure window is bounded
// to the access token's own lifetime (30 min) and to the active tab only.
const ACCESS_TOKEN_SS_KEY = 'dv_access_token';
let accessToken: string | null = null;

const isAccessTokenStillValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiryMs = (payload.exp ?? 0) * 1000;
    // Treat anything within 60s of expiry as expired so we refresh proactively.
    return expiryMs - Date.now() > 60 * 1000;
  } catch {
    return false;
  }
};

const cacheAccessToken = (token: string | null): void => {
  accessToken = token;
  if (token) {
    try { sessionStorage.setItem(ACCESS_TOKEN_SS_KEY, token); } catch {}
    API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    try { sessionStorage.removeItem(ACCESS_TOKEN_SS_KEY); } catch {}
    delete API.defaults.headers.common['Authorization'];
  }
};

// Restore the access token from sessionStorage at module load so that page
// reloads inside the same tab don't have to round-trip /accounts/refresh/.
(() => {
  try {
    const cached = sessionStorage.getItem(ACCESS_TOKEN_SS_KEY);
    if (cached && isAccessTokenStillValid(cached)) {
      accessToken = cached;
      API.defaults.headers.common['Authorization'] = `Bearer ${cached}`;
    } else if (cached) {
      sessionStorage.removeItem(ACCESS_TOKEN_SS_KEY);
    }
  } catch {}
})();

export const getAccessToken = (): string | null => accessToken;
export const setAccessToken = (token: string | null): void => { cacheAccessToken(token); };

// Flag to prevent multiple redirects
let isLoggingOut = false;

// Single-flight refresh promise. All paths that need to refresh the JWT
// (response interceptor on 401, initializeAuth on page load, ensureOrthancAuthCookie)
// share this promise. Refresh-token rotation means only ONE POST /accounts/refresh/
// can succeed per token; concurrent duplicate calls would get the second one 401'd
// and logout the user.
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.resolve(null);

  refreshPromise = (async (): Promise<string | null> => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}accounts/refresh/`, {
        refresh: refreshToken,
      });
      const newAccessToken: string = data.access;

      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }

      cacheAccessToken(newAccessToken);
      setOrthancAuthCookie(newAccessToken);
      return newAccessToken;
    } catch {
      cacheAccessToken(null);
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('username');
      return null;
    } finally {
      // Clear after microtask so simultaneous awaiters all read the same result
      setTimeout(() => { refreshPromise = null; }, 0);
    }
  })();

  return refreshPromise;
};

/**
 * No-op retained for backward compatibility with imports.
 *
 * The orthanc_auth cookie is now issued by the backend on /accounts/login/
 * and /accounts/refresh/ with HttpOnly+Secure+SameSite=Lax (pentest VUL-WEB-L06).
 * JavaScript cannot set HttpOnly cookies, so the frontend no longer touches
 * document.cookie for this value — the token is unreachable from JS, which
 * is the whole point of HttpOnly.
 */
export const setOrthancAuthCookie = (_token: string | null): void => {
  // intentional no-op — backend owns this cookie
};

const logout = async (): Promise<void> => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  // Best-effort server-side logout — blacklist the refresh token
  const refreshToken = localStorage.getItem('refresh_token');
  if (refreshToken && accessToken) {
    try {
      await axios.post(`${API_BASE_URL}accounts/logout/`,
        { refresh: refreshToken },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
    } catch {
      // Server unreachable or token already invalid — proceed with local cleanup
    }
  }

  cacheAccessToken(null);
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('username');
  setOrthancAuthCookie(null);

  window.location.href = '/login';
};

// Response interceptor: attempt token refresh on 401 before logging out
API.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh for 401 errors that haven't already been retried
    if (error.response?.status !== 401 || originalRequest._retry || isLoggingOut) {
      return Promise.reject(error);
    }

    // Don't try to refresh if this was the refresh request itself
    if (originalRequest.url?.includes('/accounts/refresh/')) {
      logout();
      return Promise.reject(error);
    }

    if (!localStorage.getItem('refresh_token')) {
      logout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    const newAccessToken = await refreshAccessToken();
    if (!newAccessToken) {
      logout();
      return Promise.reject(error);
    }

    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
    return API(originalRequest);
  }
);

export const setAuthToken = (token: string | null | undefined): void => {
  cacheAccessToken(token ?? null);
};

/**
 * Check if the user has a session (refresh token exists in localStorage).
 * Used for initial UI routing — the actual access token is restored by
 * initializeAuth() or by the 401 interceptor on the first API call.
 */
export const hasSession = (): boolean => !!localStorage.getItem('refresh_token');

/**
 * Restore the in-memory access token from the refresh token on app load.
 * Returns true if the token was restored, false if refresh failed (user must log in again).
 */
export const initializeAuth = async (): Promise<boolean> => {
  if (accessToken) return true;
  const token = await refreshAccessToken();
  return !!token;
};

/**
 * Ensure the orthanc_auth cookie is fresh before opening a viewer.
 * This proactively refreshes the token if it's close to expiry (within 5 minutes).
 * Call this before opening OHIF or any viewer that needs the cookie.
 *
 * @returns Promise that resolves when the cookie is fresh, or rejects if refresh fails
 */
export const ensureOrthancAuthCookie = async (): Promise<void> => {
  let currentToken = accessToken;
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    throw new Error('No authentication tokens found');
  }

  // After a full page navigation the in-memory access token is lost.
  // Restore it from the refresh token before proceeding.
  if (!currentToken) {
    const restored = await initializeAuth();
    if (!restored) {
      throw new Error('Failed to restore authentication from refresh token');
    }
    currentToken = accessToken;
    if (!currentToken) {
      throw new Error('Failed to restore authentication from refresh token');
    }
  }

  // Check if token is close to expiry (within 5 minutes = 300 seconds)
  // JWT tokens have format: header.payload.signature
  // The payload contains 'exp' (expiry timestamp)
  try {
    const payload = JSON.parse(atob(currentToken.split('.')[1]));
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // If more than 5 minutes until expiry, just update the cookie and return
    if (timeUntilExpiry > 5 * 60 * 1000) {
      setOrthancAuthCookie(currentToken);
      return;
    }

    // Token is close to expiry — refresh via the shared single-flight promise so
    // concurrent calls (rapid patient navigation) don't each fire their own
    // POST /accounts/refresh/ and race each other under JWT rotation.
    console.log('[api.ts] Token close to expiry, refreshing before opening viewer...');
    const newAccessToken = await refreshAccessToken();
    if (!newAccessToken) {
      throw new Error('Token refresh failed');
    }
    setOrthancAuthCookie(newAccessToken);
    console.log('[api.ts] Token refreshed and cookie updated');
  } catch (error) {
    console.error('[api.ts] Failed to ensure orthanc_auth cookie:', error);
    // Try to set the cookie with the current token anyway
    setOrthancAuthCookie(currentToken);
  }
};

export default API;
